import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { getAllGolfCourses } from '@/api/golfCourse/golfCourse.actions.ts';
import { GolfCourse } from '@/api/golfCourse/golfCourse.types.ts';
import { isApiSuccess } from '@/api/shared.types.ts';
import { CreateComplimentaryVoucherFormData } from '@/api/voucher/voucher.types.ts';
import Button from '@/components/controls/Button.tsx';
import CustomInput from '@/components/controls/CustomInput.tsx';
import CustomSelect from '@/components/controls/CustomSelect.tsx';
import { useRewardTiers } from '@/hooks/useRewardTiers.ts';

const ComplimentaryVoucherFormSchema = z.object({
  golfCourseId: z.string().min(1, 'Golf course is required'),
  validFor: z
    .string()
    .min(1, 'Event date is required')
    .refine(
      (dateString) => {
        if (!dateString) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(dateString);
        return selectedDate >= today;
      },
      { message: 'Event date cannot be in the past' }
    ),
  voucherCount: z.coerce
    .number()
    .min(1, 'Quantity must be at least 1')
    .max(100, 'Quantity cannot exceed 100'),
  rewardTierId: z.string().min(1, 'Reward tier is required'),
});

type ComplimentaryVoucherFormValues = z.infer<
  typeof ComplimentaryVoucherFormSchema
>;

type ComplimentaryVoucherModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateComplimentaryVoucherFormData) => Promise<void>;
  isLoading: boolean;
};

const ComplimentaryVoucherModal: FC<ComplimentaryVoucherModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [golfCourses, setGolfCourses] = useState<GolfCourse[]>([]);
  const { rewardTiers } = useRewardTiers();

  useEffect(() => {
    if (isOpen) {
      const fetchGolfCourses = async (): Promise<void> => {
        const response = await getAllGolfCourses();
        if (isApiSuccess(response)) {
          setGolfCourses(response.content.golfCourses);
        }
      };

      // eslint-disable-next-line no-void
      void fetchGolfCourses();
    }
  }, [isOpen]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ComplimentaryVoucherFormValues>({
    resolver: zodResolver(ComplimentaryVoucherFormSchema),
    mode: 'onChange',
    defaultValues: {
      golfCourseId: '',
      validFor: '',
      voucherCount: 1,
      rewardTierId: '',
    },
  });

  const golfCourseOptions = useMemo(
    () =>
      (golfCourses || [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((gc) => ({
          label: gc.name,
          value: gc.id,
        })),
    [golfCourses]
  );

  const rewardTierOptions = useMemo(
    () =>
      rewardTiers
        .filter((rt) => rt.status === 'active' && rt.type === 'custom')
        .map((rt) => ({
          label: `${rt.name} (R${rt.prize})`,
          value: rt.id,
        })),
    [rewardTiers]
  );

  const handleProcessSubmit = async (
    values: ComplimentaryVoucherFormValues
  ): Promise<void> => {
    await onSubmit({
      golfCourseId: values.golfCourseId,
      rewardTierId: values.rewardTierId,
      voucherCount: values.voucherCount,
      validFor: values.validFor,
    });
    reset();
    onClose();
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative bg-white rounded-lg shadow-lg p-6 w-[calc(100%-2rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-honoursOrange text-xl"
          type="button"
          aria-label="Close"
        >
          <i className="pi pi-times" />
        </button>

        <h2 className="text-2xl font-semibold mb-6 text-gray-700">
          Generate Complimentary Voucher
        </h2>

        <form
          onSubmit={handleSubmit(handleProcessSubmit)}
          className="space-y-4"
        >
          <CustomSelect
            name="golfCourseId"
            label="Select Golf Course"
            control={control}
            selectOptions={golfCourseOptions}
            placeholder="Search Golf Course..."
            divStyle="flex flex-col gap-1"
            labelStyle="text-sm font-medium text-gray-700"
            selectStyle="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
            error={errors.golfCourseId}
            mandatory
          />

          <CustomSelect
            name="rewardTierId"
            label="Reward Tier"
            control={control}
            selectOptions={rewardTierOptions}
            placeholder="Select Reward Tier"
            divStyle="flex flex-col gap-1"
            labelStyle="text-sm font-medium text-gray-700"
            selectStyle="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
            error={errors.rewardTierId}
            mandatory
          />

          <CustomInput
            name="validFor"
            label="Event Date"
            control={control}
            type="date"
            placeholder="YYYY-MM-DD"
            divStyle="flex flex-col gap-1"
            labelStyle="text-sm font-medium text-gray-700"
            inputStyle="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
            error={errors.validFor}
            mandatory
            hasIcon={false}
            disabled={isLoading}
          />

          <CustomInput
            name="voucherCount"
            label="Select Quantity"
            control={control}
            type="number"
            placeholder="Enter quantity"
            divStyle="flex flex-col gap-1"
            labelStyle="text-sm font-medium text-gray-700"
            inputStyle="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
            error={errors.voucherCount}
            mandatory
            hasIcon={false}
          />

          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
            <Button
              buttonStyle="
                justify-center items-center px-9 py-1.5 text-base font-semibold text-white
                whitespace-nowrap bg-honoursOrange rounded-xl w-full sm:w-auto
                transition-opacity duration-150
                hover:opacity-90 disabled:opacity-60
              "
              text={isLoading ? 'Generating...' : 'Generate'}
              buttonType={{ type: 'submit' }}
              disabled={isLoading}
              hasImage={false}
            />
            <Button
              buttonStyle="
                justify-center items-center px-9 py-1.5 text-base font-semibold
                border border-honoursOrange text-honoursOrange whitespace-nowrap
                bg-white rounded-xl w-full sm:w-auto
                transition-opacity duration-150
                hover:opacity-80 disabled:opacity-60
              "
              buttonType={{ type: 'button' }}
              text="Cancel"
              onClick={handleClose}
              disabled={isLoading}
              hasImage={false}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComplimentaryVoucherModal;
